import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  connectedCallback() {
    super.connectedCallback();
    const { quantityInput } = this.refs;
    quantityInput.dataset.selectorMin ??= quantityInput.min || '1';
    if (quantityInput.max) quantityInput.dataset.max ??= quantityInput.max;
    this.updateCartQuantity();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd:
        !this.refs.quantityInput.disabled &&
        quantityToAdd >= Math.max(this.getCurrentValues().min, 1) &&
        !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    quantityInput.dataset.selectorMin = min;
    quantityInput.min = min;
    if (max !== null && max !== '') {
      quantityInput.dataset.max = max;
    } else {
      delete quantityInput.dataset.max;
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;
    this.updateCartQuantity();
  }

  /**
   * Apply a server-rendered variant atomically, before clamping its selection.
   * @param {HTMLInputElement} input
   */
  updateFromInput(input) {
    const { quantityInput } = this.refs;
    const variantId = input.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    if (variantId) this.dataset.variantId = variantId;
    quantityInput.dataset.cartQuantity = input.dataset.cartQuantity || '0';
    quantityInput.dataset.min = input.dataset.min || input.min;
    quantityInput.disabled = input.disabled;
    this.updateConstraints(input.dataset.selectorMin || input.min, input.dataset.max ?? (input.max || null), input.step);
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.dataset.selectorMin ?? quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.dataset.max ?? quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, 0);
  }

  /**
   * The highest selectable quantity must also respect Shopify's increment.
   * @returns {number|null}
   */
  getSelectableMax() {
    const { min, step } = this.getCurrentValues();
    const max = this.getEffectiveMax();
    if (max === null) return null;
    if (max < min) return 0;
    return min + Math.floor((max - min) / step) * step;
  }

  /** @param {number} value @returns {number} */
  normalizeQuantity(value) {
    const { min, step } = this.getCurrentValues();
    const max = this.getSelectableMax();
    if (max !== null && max < min) return 0;
    const bounded = Math.min(max ?? Infinity, Math.max(min, value));
    return min + Math.floor((bounded - min) / step) * step;
  }

  /** Updates buttons and native input limits without changing the total variant cap. */
  updateButtonStates() {
    const { quantityInput, minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const max = this.getSelectableMax();

    quantityInput.min = String(max !== null && max < min ? 0 : min);
    if (max !== null) quantityInput.max = String(max);
    else quantityInput.removeAttribute('max');

    minusButton.disabled = quantityInput.disabled || value <= min;
    plusButton.disabled = quantityInput.disabled || (max !== null && value >= max);
  }

  /** Clamp pasted/typed values and native spinner changes as soon as they exceed stock. */
  limitQuantity() {
    const { quantityInput } = this.refs;
    const value = quantityInput.valueAsNumber;
    const max = this.getSelectableMax();
    // Keep an empty field editable; minimum/increment validation happens on blur.
    if (Number.isFinite(value) && max !== null && value > max) {
      quantityInput.value = String(max);
    }
    this.updateButtonStates();
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    if (quantityInput.disabled) return;
    const { step, value } = this.getCurrentValues();
    const newValue = this.normalizeQuantity(value + step * stepMultiplier);
    if (newValue === value) return;

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    // Non-empty value so morph's PRESERVED_ATTRIBUTES keeps the marker across a section
    // morph (its onBeforeUpdate ignores empty-valued attributes). skipsValueUpdate only
    // checks presence, not the value.
    quantityInput.setAttribute('data-skip-value-update', 'true');
    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    // Blur always ends the edit, so the morph opt-out has to be cleared before any
    // validation branch can return early. Leaving it set would make morph's
    // PRESERVED_ATTRIBUTES copy it onto every incoming server node, freezing this
    // input's displayed value against all later server renders.
    quantityInput.removeAttribute('data-skip-value-update');
    if (quantityInput.disabled) return;
    const quantity = this.normalizeQuantity(parseInt(event.target.value, 10) || 0);
    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { value } = this.getCurrentValues();
    const clampedValue = this.normalizeQuantity(value);

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
